<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { DgrCheckbox, DgrDialog } from '@stockmarkteam/donguri-ui';
import LoadableButton from '@/components/common/atoms/loadable-button.vue';
import { InsightFeedbackEvaluation } from '@/types/insights';
import {
  COMMENT_MAX_LENGTH,
  FeedbackReason,
  NEGATIVE_REASONS,
  POSITIVE_REASONS,
} from './reasons';

interface Props {
  evaluation: InsightFeedbackEvaluation;
  submitting: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  submit: [
    payload: {
      evaluation: InsightFeedbackEvaluation;
      reasons: FeedbackReason[];
      comment: string;
    },
  ];
}>();
const open = defineModel<boolean>('open', { default: false });

const selectedReasons = ref<Set<FeedbackReason>>(new Set());
const comment = ref('');

const reasons = computed(() =>
  props.evaluation === 'positive' ? POSITIVE_REASONS : NEGATIVE_REASONS,
);

const isCommentOverLimit = computed(
  () => comment.value.length > COMMENT_MAX_LENGTH,
);

const isSubmitDisabled = computed(
  () =>
    props.submitting ||
    isCommentOverLimit.value ||
    selectedReasons.value.size === 0,
);

const isReasonSelected = (value: FeedbackReason) =>
  selectedReasons.value.has(value);

const toggleReason = (value: FeedbackReason, checked: boolean) => {
  const next = new Set(selectedReasons.value);
  if (checked) {
    next.add(value);
  } else {
    next.delete(value);
  }
  selectedReasons.value = next;
};

// close-interceptor-handler は ×/背景クリック/Esc いずれの閉じる経路にも適用される
const closeInterceptor = () => !props.submitting;

watch(open, opened => {
  if (opened) {
    selectedReasons.value = new Set();
    comment.value = '';
  }
});

const handleSubmit = () => {
  if (isSubmitDisabled.value) return;
  emit('submit', {
    evaluation: props.evaluation,
    reasons: [...selectedReasons.value],
    comment: comment.value,
  });
};
</script>

<template>
  <DgrDialog v-model:open="open" :close-interceptor-handler="closeInterceptor">
    <template #description>
      <div class="content">
        <div class="header">
          <div class="c-title-bold--xm">ご意見をお聞かせください</div>
          <div class="spacing-04"></div>
          <div class="subheader c-text-regular--s">
            いただいたフィードバックは、今後の改善に役立たせていただきます
          </div>
        </div>

        <div class="reasons-section">
          <div class="checkbox-list c-text-regular--m">
            <DgrCheckbox
              v-for="reason in reasons"
              :key="reason.value"
              :model-value="isReasonSelected(reason.value)"
              @update:model-value="
                (checked: boolean) => toggleReason(reason.value, checked)
              "
            >
              {{ reason.label }}
            </DgrCheckbox>
          </div>
        </div>

        <div class="comment-section">
          <p class="comment-label c-title-bold--m">
            気付いた点や改善して欲しいことがあれば教えてください（任意）
          </p>
          <textarea
            v-model="comment"
            class="c-textArea comment-textarea"
            :class="{ 'is-error': isCommentOverLimit }"
            rows="6"
          ></textarea>
          <div class="comment-footer c-text-regular--s">
            <p v-if="isCommentOverLimit" class="comment-error">
              内容は{{ COMMENT_MAX_LENGTH }}文字まで入力できます。
            </p>
            <p
              class="comment-counter c-text-regular--m"
              :class="{ 'is-error': isCommentOverLimit }"
            >
              {{ comment.length }}/{{ COMMENT_MAX_LENGTH }}
            </p>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="dialog-footer c-text-regular--m">
        <button
          type="button"
          class="c-btn c-btn--auto c-btnOutline"
          :class="{ disabled: submitting }"
          :disabled="submitting"
          @click="open = false"
        >
          キャンセル
        </button>
        <LoadableButton
          type="button"
          class="c-btn c-btn--auto c-btn--AnewsPrimary submit-button"
          :class="{ disabled: isSubmitDisabled }"
          :disabled="isSubmitDisabled"
          :is-loading="submitting"
          @click="handleSubmit"
        >
          フィードバックを送信する
        </LoadableButton>
      </div>
    </template>
  </DgrDialog>
</template>

<style lang="scss" scoped>
.content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 448px;
}

.subheader {
  color: $color-gray600;
}

.reasons-section {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .checkbox-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}

.comment-section {
  display: flex;
  flex-direction: column;
  gap: 4px;

  .comment-label {
    margin: 0;
  }

  .comment-textarea {
    width: 100%;

    &.is-error {
      border-color: $color-orange1000;
    }
  }

  .comment-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .comment-error {
      color: $color-orange1000;
      margin: 0;
    }

    .comment-counter {
      margin: 0 0 0 auto;
      color: $color-gray1000;

      &.is-error {
        color: $color-orange1000;
      }
    }
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
}

.submit-button {
  min-width: 200px;
}
</style>
